
import { defineComponent, PropType, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import ProgressSpinner from 'primevue/progressspinner';
import Tooltip from 'primevue/tooltip';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { TEMPLATE_MEDIA_PREVIEW } from '@/composables/flows/useTemplateConfig';
import { UserState } from '@/composables/User';
import { isDisplayTemplate } from '@/components/template-builder/utils/helpers';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError } from '@/helpers';
import { store } from '@/store';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';

interface DropdownItem {
  label: string;
  value: number;
}

interface DropdownGroup {
  label: string;
  items: DropdownItem[];
}

/**
 * Extract game prize template IDs from root template's template_config.
 * Prize templateIds may be base64 encoded or raw numbers.
 */
const extractGamePrizeTemplateIds = (rootConfig: any): Set<number> => {
  const ids = new Set<number>();
  if (!rootConfig.hasGame || !rootConfig.games_data) return ids;

  const gameTypes = ['wheel', 'slotMachine', 'scratchCard'];
  for (const gameType of gameTypes) {
    const prizes = rootConfig.games_data[gameType]?.prizes;
    if (!prizes) continue;
    for (const prize of prizes) {
      if (!prize.templateId) continue;
      // templateId may be base64 encoded
      try {
        const decoded = Number(atob(String(prize.templateId)));
        if (!Number.isNaN(decoded) && decoded > 0) {
          ids.add(decoded);
          continue;
        }
      } catch { /* not base64, try raw */ }
      const raw = Number(prize.templateId);
      if (!Number.isNaN(raw) && raw > 0) {
        ids.add(raw);
      }
    }
  }
  return ids;
};

/**
 * Parse imageKey which may be a JSON array (new format) or a plain string (old format).
 */
const getFirstImageKey = (imageKey: string | undefined | null): string => {
  if (!imageKey) return '';
  try {
    const parsed = JSON.parse(imageKey);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed[0];
    }
  } catch {
    // Not JSON, return as is
  }
  return imageKey;
};

export default defineComponent({
  name: 'TemplatePreview',

  components: {
    Dropdown,
    Button,
    Menu,
    ProgressSpinner,
    TemplateHtmlPreviewSlider,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    templates: {
      type: Array as PropType<TemplatesCustom[]>,
      required: true,
      default: () => [],
    },

    activeMedia: {
      type: String as PropType<'desktop' | 'mobile'>,
      default: 'desktop',
    },

    activeTemplateIndex: {
      type: Number,
      default: 0,
    },

    imageLoadTime: {
      type: Object as PropType<Record<number, number>>,
      default: () => ({}),
    },

    imageRefreshKey: {
      type: Number,
      default: 0,
    },

    showSendTest: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'tab-change',
    'select-media',
    'change-template',
    'edit-template',
    'send-test',
    'image-loaded',
    'image-failed',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const menuRef = ref();
    const baseImagePreviewUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;
    const idShop = UserState.activeShop?.id || 0;

    // Get current active template
    const activeTemplate = computed(() => props.templates[props.activeTemplateIndex] || null);

    // Check if current image has loaded
    const isImageReady = computed(() => {
      if (!activeTemplate.value) return false;
      const time = props.imageLoadTime[activeTemplate.value.id_template];
      return !!time && time > 0;
    });

    // Build grouped dropdown options from templates list
    const dropdownOptions = computed((): DropdownItem[] | DropdownGroup[] => {
      const items: DropdownItem[] = props.templates.map((tmpl, index) => ({
        label: tmpl.label || `Template ${tmpl.id_template}`,
        value: index,
      }));

      if (props.templates.length <= 1) return items;

      // Check if root template has game data
      let rootConfig: any = {};
      try {
        rootConfig = JSON.parse(props.templates[0]?.template_config ?? '{}');
      } catch { /* invalid JSON */ }

      const gamePrizeIds = extractGamePrizeTemplateIds(rootConfig);

      // No game → flat list (no groups)
      if (gamePrizeIds.size === 0) return items;

      // With game → grouped
      const steps = items.filter((_, i) => !gamePrizeIds.has(props.templates[i].id_template));
      const gameResults = items.filter((_, i) => gamePrizeIds.has(props.templates[i].id_template));

      const groups: DropdownGroup[] = [];
      if (steps.length) {
        groups.push({
          label: t('automatedScenarios.actions.sending_channel.template_preview.steps'),
          items: steps,
        });
      }
      if (gameResults.length) {
        groups.push({
          label: t('automatedScenarios.actions.sending_channel.template_preview.gameResults'),
          items: gameResults,
        });
      }
      return groups;
    });

    // Whether the dropdown uses grouped options
    const hasGroups = computed(() => {
      const opts = dropdownOptions.value;
      return opts.length > 0 && 'items' in opts[0];
    });

    // Check if desktop preview is available for template type
    const canShowDesktop = (type: string | null | undefined): boolean => {
      if (!type) return false;
      return TEMPLATE_MEDIA_PREVIEW.desktop.includes(type);
    };

    // Check if mobile preview is available for template type
    const canShowMobile = (type: string | null | undefined): boolean => {
      if (!type) return false;
      return TEMPLATE_MEDIA_PREVIEW.mobile.includes(type);
    };

    // Get image URL for template preview (with getFirstImageKey support)
    const getImageUrl = (template: TemplatesCustom, isMobile: boolean): string => {
      const imageKey = getFirstImageKey(template.imageKey);
      const suffix = isDisplayTemplate(template.type ?? undefined) ? '-isdisplay' : '';
      const responsiveSuffix = isMobile ? '-responsive' : '';
      const width = isMobile ? 300 : 600;
      const timestamp = props.imageLoadTime[template.id_template] || new Date().getTime();
      return `${baseImagePreviewUrl}${idShop}_${imageKey}${suffix}${responsiveSuffix}.png&w=${width}&${timestamp}`;
    };

    // Toggle menu
    const toggleMenu = (event: Event) => {
      if (menuRef.value) {
        menuRef.value.toggle(event);
      }
    };

    // Handle dropdown selection change
    const onDropdownChange = (event: { value: number }) => {
      emit('tab-change', event.value);
    };

    // Action handlers
    const handleEditTemplate = () => {
      if (activeTemplate.value) {
        emit('edit-template', activeTemplate.value.id_template, activeTemplate.value.type, activeTemplate.value.label);
      }
    };

    const handleChangeTemplate = () => {
      if (activeTemplate.value) {
        emit('change-template', activeTemplate.value.id_template, activeTemplate.value.type);
      }
    };

    // ========================================================================
    // HTML Preview
    // ========================================================================
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);

    const openHtmlPreview = async () => {
      if (!activeTemplate.value) return;

      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const idTemplateB64 = btoa(String(activeTemplate.value.id_template));
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/html-preview-selector',
          {},
          '',
          { idShop, idTemplate: idTemplateB64 },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(result?.error || t('automatedScenarios.actions.sending_channel.template_preview.htmlPreviewError'));
        }
      } catch {
        showToastError(t('automatedScenarios.actions.sending_channel.template_preview.htmlPreviewError'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    return {
      t,
      menuRef,
      activeTemplate,
      isImageReady,
      dropdownOptions,
      hasGroups,
      canShowDesktop,
      canShowMobile,
      getImageUrl,
      toggleMenu,
      onDropdownChange,
      handleEditTemplate,
      handleChangeTemplate,
      htmlPreviewData,
      openHtmlPreview,
      closeHtmlPreview,
    };
  },
});
